import { useMemo } from 'react'
import { MetricCombobox } from '@/components/custom/metrics/metric-combobox'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useScorecardMetricOptions } from '@/hooks/use-scorecard'
import type { OgsmBoardNode } from '@/types/api'

/**
 * Links a node at any depth to one of the company's metrics, or unlinks it. Only
 * offered when the backend says the link can change (`canLinkMetric`).
 */
export function LinkMetricDialog({
  node,
  open,
  onOpenChange,
}: {
  node: OgsmBoardNode
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { companyId, saveNode } = useBoardEdit()
  const { metricLinking } = useOgsmApi(companyId)
  const metrics = useScorecardMetricOptions(
    companyId,
    'MONTHLY',
    open && metricLinking,
    node.metricLabelId ?? undefined
  )
  const options = useMemo(
    () =>
      (metrics.data?.options ?? []).map((option) => ({
        id: option.metricLabelId,
        label: option.labeledAs,
        labeledAs: option.labeledAs,
        sectionName: option.globalSectionName ?? 'Metrics',
        category: option.metricType?.category,
        description:
          option.metricType?.name ?? option.globalSectionName ?? undefined,
        sortOrder: option.sortOrder ?? null,
      })),
    [metrics.data]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-105 px-4 py-5">
        <DialogHeader className="mb-4">
          <DialogTitle>{OGSM_COPY.linkMetric}</DialogTitle>
          <DialogDescription>{OGSM_COPY.metricDialogHint}</DialogDescription>
        </DialogHeader>
        <MetricCombobox
          options={options}
          value={node.metricLabelId ?? undefined}
          isLoading={metrics.isLoading}
          onValueChange={(metricLabelId) => {
            onOpenChange(false)
            saveNode(node.id, { metricLabelId })
          }}
        />
        <div className="mt-4 flex justify-end gap-2">
          {node.metricLabelId && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false)
                saveNode(node.id, { metricLabelId: null })
              }}
            >
              {OGSM_COPY.unlinkMetric}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {OGSM_COPY.cancel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
